import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const assessmentTypes = [
  {
    id: "growth-mindset",
    title: "Growth Mindset Check",
    description: "Reflect on your current mindset and growth potential",
    questions: [
      { id: "challenges", text: "How do you typically respond to challenges?", type: "text" },
      { id: "feedback", text: "How do you feel when receiving constructive feedback?", type: "text" },
      { id: "success", text: "What does success mean to you right now?", type: "text" }
    ]
  },
  {
    id: "daily-habits",
    title: "Daily Habits Reflection",
    description: "Evaluate your current habits and routines",
    questions: [
      { id: "morning", text: "Describe your typical morning routine", type: "text" },
      { id: "energy", text: "When do you feel most energized during the day?", type: "text" },
      { id: "improvement", text: "What's one habit you'd like to improve?", type: "text" }
    ]
  },
  {
    id: "positivity-meter",
    title: "Positivity Meter",
    description: "Assess your current positivity and wellbeing",
    questions: [
      { id: "grateful", text: "What are three things you're grateful for today?", type: "text" },
      { id: "accomplishment", text: "What recent accomplishment are you proud of?", type: "text" },
      { id: "looking-forward", text: "What are you looking forward to?", type: "text" }
    ]
  }
];

export default function Assessments({ user, onLogout }) {
  const navigate = useNavigate();
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [pastAssessments, setPastAssessments] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPastAssessments();
  }, []);

  const fetchPastAssessments = async () => {
    try {
      const response = await axios.get(`${API}/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPastAssessments(response.data);
    } catch (error) {
      console.error("Error fetching assessments:", error);
    }
  };

  const handleStartAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setResponses({});
    setShowResults(false);
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/assessments`,
        {
          assessment_type: selectedAssessment.title,
          responses: responses
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCurrentResult(response.data);
      setShowResults(true);
      toast.success("Assessment completed!");
      fetchPastAssessments();
    } catch (error) {
      toast.error("Failed to submit assessment");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedAssessment(null);
    setShowResults(false);
    setCurrentResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50" data-testid="assessments-page">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate("/dashboard")} variant="ghost" size="sm" className="rounded-full" data-testid="back-to-dashboard-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Button>
              <h1 className="text-2xl font-extrabold text-slate-800">
                Self-Assessments
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{user.name}</span>
              <Button onClick={onLogout} variant="outline" size="sm" className="rounded-full" data-testid="assessments-logout-btn">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        {!selectedAssessment && !showResults ? (
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold mb-4">Choose an Assessment</h2>
              <p className="text-lg text-slate-600">Take a moment to reflect on your growth journey</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {assessmentTypes.map((assessment, index) => (
                <motion.div
                  key={assessment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 card-hover cursor-pointer border-2 hover:border-orange-300 h-full flex flex-col" onClick={() => handleStartAssessment(assessment)} data-testid={`assessment-type-${assessment.id}`}>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{assessment.title}</h3>
                      <p className="text-slate-600 mb-4">{assessment.description}</p>
                      <p className="text-sm text-slate-500">{assessment.questions.length} questions</p>
                    </div>
                    <Button className="btn-primary mt-4 w-full" data-testid={`start-assessment-${assessment.id}-btn`}>
                      Start Assessment
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>

            {pastAssessments.length > 0 && (
              <div>
                <h3 className="text-2xl font-bold mb-6">Past Assessments</h3>
                <div className="space-y-4">
                  {pastAssessments.map((assessment, index) => (
                    <motion.div
                      key={assessment.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="p-6 border-2" data-testid={`past-assessment-${index}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-bold">{assessment.assessment_type}</h4>
                            <p className="text-sm text-slate-500">
                              {new Date(assessment.created_at).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-sm font-semibold">
                            Completed
                          </span>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-slate-700 leading-relaxed">{assessment.analysis}</p>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : showResults && currentResult ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8 border-2 border-green-200 bg-gradient-to-br from-green-50 to-white" data-testid="assessment-results">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold mb-2">Assessment Complete!</h2>
                <p className="text-slate-600">Here's your personalized insight</p>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <p className="text-lg text-slate-700 leading-relaxed">{currentResult.analysis}</p>
              </div>

              <div className="flex gap-4">
                <Button onClick={handleBackToList} className="btn-secondary flex-1" data-testid="back-to-assessments-btn">
                  Back to Assessments
                </Button>
                <Button onClick={() => navigate("/dashboard")} className="btn-primary flex-1" data-testid="results-go-dashboard-btn">
                  Go to Dashboard
                </Button>
              </div>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8">
              <div className="mb-6">
                <Button onClick={handleBackToList} variant="ghost" size="sm" className="mb-4" data-testid="cancel-assessment-btn">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back
                </Button>
                <h2 className="text-3xl font-bold mb-2">{selectedAssessment.title}</h2>
                <p className="text-slate-600">{selectedAssessment.description}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {selectedAssessment.questions.map((question, index) => (
                  <div key={question.id} className="space-y-2">
                    <Label className="text-lg font-semibold">
                      {index + 1}. {question.text}
                    </Label>
                    <Textarea
                      required
                      value={responses[question.id] || ""}
                      onChange={(e) => handleResponseChange(question.id, e.target.value)}
                      placeholder="Type your response here..."
                      rows={4}
                      className="w-full rounded-xl border-slate-200 focus:border-orange-500"
                      data-testid={`assessment-question-${question.id}`}
                    />
                  </div>
                ))}

                <Button type="submit" disabled={loading} className="btn-primary w-full text-lg py-6" data-testid="submit-assessment-btn">
                  {loading ? "Analyzing..." : "Submit Assessment"}
                </Button>
              </form>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}